import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { AstroUtilsService } from "@core/services/astro-utils/astro-utils.service";
import { MeasurementUtilsService } from "@core/services/measurement-utils/measurement-utils.service";
import {
  MeasurementData,
  MeasurementPoint,
  SolutionMatrix
} from "@shared/components/misc/measuring-tool/measuring-tool.component";

@Injectable({
  providedIn: "root"
})
export class MeasurementRenderingService {
  private readonly _isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly astroUtilsService: AstroUtilsService,
    private readonly measurementUtilsService: MeasurementUtilsService
  ) {
    this._isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Check if the solution matrix has all required properties for valid coordinates
   */
  isValidSolutionMatrix(solutionMatrix: SolutionMatrix | null): boolean {
    if (!this._isBrowser || !solutionMatrix) {
      return false;
    }

    return this.astroUtilsService.isValidSolutionMatrix(solutionMatrix);
  }

  /**
   * Calculate celestial coordinates at a given screen position
   */
  calculateCoordinatesAtPoint(
    x: number,
    y: number,
    solutionMatrix: SolutionMatrix | null,
    imageElement: HTMLElement
  ): { ra: number; dec: number } | null {
    // Never attempt to access DOM in server-side rendering
    if (!this._isBrowser || !this.isValidSolutionMatrix(solutionMatrix)) {
      return null;
    }

    if (!imageElement) {
      console.error("Cannot locate image element for coordinate calculation");
      return null;
    }

    try {
      // Verify the image element is properly positioned
      const rect = imageElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        console.error("Invalid image element dimensions:", rect);
        return null;
      }

      // Use the astro utils service to calculate coordinates
      const result = this.astroUtilsService.calculateCoordinatesAtPoint(x, y, solutionMatrix, imageElement);

      // Validate result
      if (result && (isNaN(result.ra) || isNaN(result.dec))) {
        console.error("Invalid coordinate result:", result);
        return null;
      }

      return result;
    } catch (error) {
      console.error("Error calculating coordinates:", error);
      return null;
    }
  }

  /**
   * Calculate angular distance between two measurement points
   */
  calculateAngularDistance(startPoint: MeasurementPoint, endPoint: MeasurementPoint): string {
    // We only support measurements with a valid solution matrix
    if (!this._isBrowser) {
      return "";
    }

    // If we have coordinates for both points, use them to calculate the actual angular distance
    if (startPoint?.ra !== null && startPoint?.dec !== null && endPoint?.ra !== null && endPoint?.dec !== null) {
      const angularDistance = this.astroUtilsService.calculateAngularDistance(
        startPoint.ra,
        startPoint.dec,
        endPoint.ra,
        endPoint.dec
      );

      // Format the angular distance using the service's stabilized formatter
      return this.astroUtilsService.formatAngularDistance(angularDistance);
    }

    return "";
  }

  /**
   * Format astronomical angle (wrapper method for service)
   */
  formatAstronomicalAngle(arcseconds: number): string {
    return this.astroUtilsService.formatAstronomicalAngle(arcseconds);
  }

  /**
   * Calculate distances in celestial coordinates for rectangle dimensions
   */
  getHorizontalCelestialDistance(
    minX: number,
    maxX: number,
    y: number,
    solutionMatrix: SolutionMatrix | null,
    imageElement: HTMLElement
  ): string {
    // Skip if not browser or no valid solution
    if (!this._isBrowser || !this.isValidSolutionMatrix(solutionMatrix)) {
      return "";
    }

    try {
      // Calculate coordinates at both ends of the horizontal line
      const leftCoords = this.calculateCoordinatesAtPoint(minX, y, solutionMatrix, imageElement);
      const rightCoords = this.calculateCoordinatesAtPoint(maxX, y, solutionMatrix, imageElement);

      // Calculate angular distance if both coordinates are valid
      if (leftCoords && rightCoords) {
        const angularDistance = this.astroUtilsService.calculateAngularDistance(
          leftCoords.ra,
          leftCoords.dec,
          rightCoords.ra,
          rightCoords.dec
        );

        // Convert to arcseconds and format
        const arcseconds = angularDistance * 3600;
        return this.astroUtilsService.formatAstronomicalAngle(arcseconds);
      }
    } catch (error) {
      console.error("Error calculating horizontal celestial distance:", error);
    }

    return "";
  }

  /**
   * Calculate vertical distance in celestial coordinates
   */
  getVerticalCelestialDistance(
    x: number,
    minY: number,
    maxY: number,
    solutionMatrix: SolutionMatrix | null,
    imageElement: HTMLElement
  ): string {
    // Skip if not browser or no valid solution
    if (!this._isBrowser || !this.isValidSolutionMatrix(solutionMatrix)) {
      return "";
    }

    try {
      // Calculate coordinates at both ends of the vertical line
      const topCoords = this.calculateCoordinatesAtPoint(x, minY, solutionMatrix, imageElement);
      const bottomCoords = this.calculateCoordinatesAtPoint(x, maxY, solutionMatrix, imageElement);

      // Calculate angular distance if both coordinates are valid
      if (topCoords && bottomCoords) {
        const angularDistance = this.astroUtilsService.calculateAngularDistance(
          topCoords.ra,
          topCoords.dec,
          bottomCoords.ra,
          bottomCoords.dec
        );

        // Convert to arcseconds and format
        const arcseconds = angularDistance * 3600;
        return this.astroUtilsService.formatAstronomicalAngle(arcseconds);
      }
    } catch (error) {
      console.error("Error calculating vertical celestial distance:", error);
    }

    return "";
  }

  /**
   * Update label positions for a measurement with collision avoidance
   */
  updateLabelPositions(measurement: MeasurementData): void {
    // This delegates to the measurement utils service
    this.measurementUtilsService.updateCoordinateLabelPositions(measurement);
  }

  /**
   * Calculate the pixel distance between two points
   */
  calculatePixelDistance(startX: number, startY: number, endX: number, endY: number): number {
    return Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
  }

  /**
   * Reposition a point using celestial coordinates after a resize or orientation change
   */
  repositionPointWithSkyCoords(
    point: MeasurementPoint,
    solutionMatrix: SolutionMatrix | null,
    imageElement: HTMLElement
  ): { x: number; y: number } | null {
    if (
      !this._isBrowser ||
      !point ||
      point.ra === null ||
      point.dec === null ||
      !this.isValidSolutionMatrix(solutionMatrix) ||
      !imageElement
    ) {
      return null;
    }

    try {
      // Use the utility to convert celestial coordinates back to pixel coordinates
      const newPosition = this.astroUtilsService.calculatePixelPositionFromCoordinates(
        point.ra,
        point.dec,
        solutionMatrix,
        imageElement
      );

      if (newPosition) {
        return {
          x: newPosition.x,
          y: newPosition.y
        };
      }
    } catch (error) {
      console.error("Error repositioning point with sky coordinates:", error);
    }

    return null;
  }

  /**
   * Check if a point is outside the boundaries of the image
   */
  checkPointOutsideBoundaries(point: { x: number; y: number }, imageElement: HTMLElement): boolean {
    if (!this._isBrowser || !imageElement) {
      return false;
    }

    const rect = imageElement.getBoundingClientRect();

    return point.x < rect.left || point.x > rect.right || point.y < rect.top || point.y > rect.bottom;
  }

  /**
   * Encode measurements for URL sharing (delegate to existing service)
   */
  encodeMeasurementsForUrl(measurements: MeasurementData[]): string {
    return this.measurementUtilsService.encodeMeasurementsForUrl(measurements);
  }

  /**
   * Calculate midpoint between two points
   */
  calculateMidPoint(startX: number, startY: number, endX: number, endY: number): { x: number; y: number } {
    return {
      x: (startX + endX) / 2,
      y: (startY + endY) / 2
    };
  }
}
